/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, computed, observable } from 'mobx';
import { UIConfigsStore } from 'src/core/stores/UIConfigsStore';
import { QnaMakerQnaPairsUiStore } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerQnaPairsUiStore';
import { QnaMakerQnaSwagger } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';
import { QnaMakerSourceStore } from 'src/qnaMaker/core/stores/QnaMakerSourceStore';

export class QnaMakerSuggestionsUiStore extends QnaMakerQnaPairsUiStore {
    @observable.shallow public qnaPairsSelectedSuggestionObj: { [key: number]: string[] } = {};

    constructor(portalUiStore: UIConfigsStore, _qnaPairsJson: QnaMakerQnaSwagger[] = [], sourceStores: QnaMakerSourceStore[] = []) {
        super(portalUiStore, _qnaPairsJson, sourceStores);
        _qnaPairsJson?.forEach(pair => {
            this.qnaPairsSelectedSuggestionObj[pair.id] = [];
        });
    }

    @computed get qnaPairsSuggestionsJson() {
        // Splitting the pairs with both smart suggestions and active learning suggestions into two items.
        const suggestionsPairs: QnaMakerQnaSwagger[] = [];
        this.qnaPairsJson.forEach(qna => {
            if (!qna.activeLearningSuggestions.length) return;
            const smartSuggestionsCount = qna.activeLearningSuggestions.filter(s => s.totalAutoSuggestionCountFromGenerativeModel).length;
            if (smartSuggestionsCount) {
                const smartSuggestions = qna.activeLearningSuggestions.filter(s => s.totalAutoSuggestionCountFromGenerativeModel);
                const activeLearningSuggestions = qna.activeLearningSuggestions.filter(s => !s.totalAutoSuggestionCountFromGenerativeModel);
                if (activeLearningSuggestions.length) {
                    suggestionsPairs.push({
                        ...qna,
                        activeLearningSuggestions
                    });
                }
                suggestionsPairs.push({
                    ...qna,
                    activeLearningSuggestions: smartSuggestions
                });
            } else {
                suggestionsPairs.push(qna);
            }
        });

        return suggestionsPairs;
    }

    @computed
    public get areAllSuggestionsSelected() {
        return (
            Object.values(this.qnaPairsSelectedSuggestionObj).some(v => v.length) &&
            this.displayedQnaPairStores.every(qnaPair =>
                qnaPair.activeLearningSuggestions.every(a => this.qnaPairsSelectedSuggestionObj[qnaPair.id].find(s => s === a.clusterHead))
            )
        );
    }

    @computed
    public get areSomeSuggestionsSelected() {
        return Object.values(this.qnaPairsSelectedSuggestionObj).some(arr => arr.length);
    }

    @computed
    public get selectedSuggestionsKeys() {
        return [
            ...new Set(Object.keys(this.qnaPairsSelectedSuggestionObj).filter(k => this.qnaPairsSelectedSuggestionObj[Number(k)].length))
        ];
    }

    @action
    public changeSuggestionSelection(qnaId: number, suggestion: string, isSelected: boolean) {
        const existingPair = this.qnaPairsSelectedSuggestionObj[qnaId];
        const filterOutIncomingSuggestion = existingPair?.filter(s => s !== suggestion) ?? [];
        this.qnaPairsSelectedSuggestionObj = {
            ...this.qnaPairsSelectedSuggestionObj,
            [qnaId]: [...filterOutIncomingSuggestion, isSelected && suggestion].filter(Boolean)
        };
    }

    @action
    public changeAllSuggestionSelection(isSelected: boolean) {
        const _selectionObj = { ...this.qnaPairsSelectedSuggestionObj };
        this.displayedQnaPairStores.forEach(d => {
            _selectionObj[d.id] = isSelected ? [..._selectionObj[d.id], ...d.activeLearningSuggestions.map(a => a.clusterHead)] : [];
        });
        this.qnaPairsSelectedSuggestionObj = _selectionObj;
    }

    @action
    public resetSuggestionSelection() {
        const _selectionObj = { ...this.qnaPairsSelectedSuggestionObj };
        this.displayedQnaPairStores.forEach(d => {
            _selectionObj[d.id] = [];
        });
        this.qnaPairsSelectedSuggestionObj = _selectionObj;
    }

    @action
    public setQnaPairsJson(_qnaPairsJson: QnaMakerQnaSwagger[]) {
        _qnaPairsJson?.filter(Boolean).forEach(pair => {
            this.qnaPairsSelectedSuggestionObj[pair.id] = [];
        });
        super.setQnaPairsJson(_qnaPairsJson);
    }

    @action
    public acceptOrRejectSelectedQnaPairsSuggestions(areSuggestionsAccepted: boolean) {
        const updatedSwaggers: QnaMakerQnaSwagger[] = [];
        this.qnaPairsJson = this.qnaPairsJson.map(qna => {
            if (qna.activeLearningSuggestions.length && this.qnaPairsSelectedSuggestionObj[qna.id]?.length) {
                const updatedObject: QnaMakerQnaSwagger = {
                    ...qna,
                    activeLearningSuggestions: qna.activeLearningSuggestions.filter(
                        s => !this.qnaPairsSelectedSuggestionObj[qna.id].find(q => q === s.clusterHead)
                    ),
                    questions: areSuggestionsAccepted
                        ? [
                              ...qna.questions,
                              ...qna.activeLearningSuggestions
                                  .filter(s => this.qnaPairsSelectedSuggestionObj[qna.id].find(q => q === s.clusterHead))
                                  .map(s => s.clusterHead)
                          ]
                        : qna.questions
                };
                updatedSwaggers.push(updatedObject);

                return updatedObject;
            }

            return qna;
        });
        this.dirtyQnaPairs = updatedSwaggers.map(swagger => ({ op: 'replace', value: swagger }));
    }

    @action
    public acceptOrRejectDisplayedQnaPairsSuggestions(areSuggestionsAccepted: boolean) {
        const displayedStoreIds = this.displayedQnaPairStores.map(d => d.id);
        const updatedSwaggers: QnaMakerQnaSwagger[] = [];
        this.qnaPairsJson = this.qnaPairsJson.map(qna => {
            if (qna.activeLearningSuggestions.length && displayedStoreIds.includes(qna.id)) {
                const updatedObject: QnaMakerQnaSwagger = {
                    ...qna,
                    activeLearningSuggestions: [],
                    questions: areSuggestionsAccepted
                        ? [...qna.questions, ...qna.activeLearningSuggestions.map(s => s.clusterHead)]
                        : qna.questions
                };
                updatedSwaggers.push(updatedObject);
                return updatedObject;
            }
            return qna;
        });
        this.dirtyQnaPairs = updatedSwaggers.map(swagger => ({ op: 'replace', value: swagger }));
    }
}
